<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { useVuelidate } from '@vuelidate/core';
import CheckBox from 'v3/components/Form/CheckBox.vue';

export default {
  components: {
    CheckBox,
  },
  props: {
    selectedResponse: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      title: '',
      description: '',
      showOnSidebar: true,
      color: '',
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    pageTitle() {
      return `${this.$t('LABEL_MGMT.EDIT.TITLE')} - ${
        this.selectedResponse.title
      }`;
    },
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
  },
  mounted() {
    this.setFormValues();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    setFormValues() {
      this.title = this.selectedResponse.title;
      this.description = this.selectedResponse.description;
      this.showOnSidebar = this.selectedResponse.show_on_sidebar;
      this.color = this.selectedResponse.color;
    },
    onShowOnSidebarUpdate(_, checked) {
      this.showOnSidebar = checked;
    },
    editLabel() {
      this.$store
        .dispatch('labels/update', {
          id: this.selectedResponse.id,
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
        })
        .then(() => {
          useAlert(this.$t('LABEL_MGMT.EDIT.API.SUCCESS_MESSAGE'), {type: 'success'});
          setTimeout(() => this.onClose(), 10);
        })
        .catch(error => {
          const errorMessage =
            error?.message || this.$t('LABEL_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage, { type: 'error' });
        });
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex gap-6 flex-wrap mx-0" @submit.prevent="editLabel" @keydown.enter.prevent>
      <woot-input
        v-model="title"
        :required="true"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input input-form"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        data-testid="label-title"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />

      <woot-input
        v-model="description"
        :class="{ error: v$.description.$error }"
        class="w-full input-form"
        :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        data-testid="label-description"
        @input="v$.description.$touch"
        @blur="v$.description.$touch"
      />

      <div class="w-full">
        <label class="text-slate-700 dark:text-slate-300">
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="color" class="cp-round"/>
        </label>
      </div>

      <div class="flex items-center w-full gap-2">
        <CheckBox
          :is-checked="showOnSidebar"
          value="show_on_sidebar"
          @update="onShowOnSidebarUpdate"
        />
        <label for="conversation_creation" class="text-slate-700 dark:text-slate-300">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>

      <div class="flex items-center justify-center w-full gap-3 px-0 py-2">
        <button class="input-base bg-slate-100 dark:bg-slate-700 !text-slate-700 dark:!text-slate-300 rounded-lg" @click.prevent="onClose">
          {{ $t('LABEL_MGMT.FORM.CANCEL') }}
        </button>
        <woot-button
          class="!bg-grass-500 !text-white rounded-lg"
          :is-disabled="v$.title.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
          data-testid="label-submit"
        >
          {{ $t('LABEL_MGMT.FORM.EDIT') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  ::v-deep {
    input {
      @apply lowercase;
    }
  }
}

.input-form :deep(input) {
  @apply bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 rounded-lg;
}

.cp-round :deep(> *:first-child) {
  width: 2rem;            
  height: 2rem;           
  border-radius: 9999px;  
  overflow: hidden;

  border: 0 !important;
  box-shadow: none !important;
  outline: none !important;
}

.cp-round :deep(.popover),
.cp-round :deep([role='dialog']) {
  z-index: 50;
}
</style>
